// R&M Dashboard: repair and maintenance in one place, by day or month and by cost
// center. Reads the fleet reports API through /fleet-dashboard (ADR-0027), which
// scopes each person to their cost centers (backend/src/routes/fleetDashboard.js).
// A scoped person has no "All cost centers", Parts is company-wide and so only for
// those who see every cost center, and mechanic names need their own permission.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../services/api';
import PageHeader from '../components/Shared/PageHeader';
import { fmtDate, fmtMoney } from '../components/Inventory/format';
import { c, card, sp, t } from '../theme';

const TABS = [
  { id: 'today', label: 'Today' },
  { id: 'yards', label: 'Yards' },
  { id: 'costs', label: 'Costs' },
  { id: 'shop', label: 'Mechanics & Shop' },
  { id: 'trucks', label: 'Trucks' },
  { id: 'parts', label: 'Parts' },
  { id: 'pm', label: 'PM & Compliance' },
];

const COST_VIEWS = [
  { id: 'category', label: 'By category', by: 'TOTAL', head: 'Category' },
  { id: 'yard', label: 'By cost center', by: 'YARD', head: 'Cost center' },
  { id: 'class', label: 'By asset class', by: 'ASSET_CLASS', head: 'Asset class' },
  { id: 'system', label: 'By truck system', by: 'SYSTEM', head: 'Truck system' },
];

// What in-house and vendor money adds up to, so the rows sum to the total.
const CATEGORIES = [
  ['In-house labor', 'in_house_labor'],
  ['In-house parts', 'in_house_parts'],
  ['In-house other charges', 'in_house_other'],
  ['Outside vendors', 'vendor_cost'],
];

// Task groups in the stack, scored work first (fleet-reporting rule R22).
const LABOR_GROUPS = [
  ['Direct Repair', '#2F5DA8'],
  ['PM', '#6F97D6'],
  ['Bin Repair', '#A9C2E8'],
  ['Road Call', '#7A5AA6'],
  ['MRF Plant', '#5B7083'],
  ['Bulk', '#B8A06A'],
  ['Indirect', '#E0792B'],
  ['Not Coded', '#9CA3AF'],
];
const SCORED_GROUPS = new Set(['Direct Repair', 'PM', 'Bin Repair']);
const TASK_MIN_JOBS = 5;
const minHours = (grain) => (grain === 'DAY' ? 20 : 100);

const PLANNED = '#2F5DA8';
const UNPLANNED = '#E0792B';
const UNKNOWN = '#9CA3AF';

const n = (v) => Number(v || 0);

function pacificToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
}

// Last 14 days, or this month and the 11 before it.
export function rangeFor(grain, today = pacificToday()) {
  const end = new Date(`${today}T00:00:00Z`);
  const start = new Date(end);
  if (grain === 'DAY') start.setUTCDate(start.getUTCDate() - 13);
  else {
    start.setUTCDate(1);
    start.setUTCMonth(start.getUTCMonth() - 11);
  }
  return { from: start.toISOString().slice(0, 10), to: today };
}

function periodLabel(period, grain) {
  const d = new Date(`${String(period).slice(0, 10)}T00:00:00Z`);
  return d.toLocaleDateString('en-US', grain === 'DAY'
    ? { month: 'short', day: 'numeric', timeZone: 'UTC' }
    : { month: 'short', year: '2-digit', timeZone: 'UTC' });
}

function pct(part, whole) {
  return whole > 0 ? `${Math.round((part / whole) * 100)}%` : '–';
}

// A tile with onOpen is a button that opens the list behind its number; split is the
// Main and MRF breakout, on its own line.
function Tile({ label, value, note, split, onOpen, action = 'See the list' }) {
  const body = (
    <>
      <div style={s.tileLabel}>{label}</div>
      <div style={s.tileValue}>{value}</div>
      <div style={s.tileNote}>{note}</div>
      {split && <div style={s.tileNote}>{split}</div>}
      {onOpen && <div style={s.tileAction}>{action}</div>}
    </>
  );
  if (!onOpen) return <div style={s.tile}>{body}</div>;
  return <button type="button" onClick={onOpen} style={s.tileButton}>{body}</button>;
}

function Switch({ options, value, onChange, label }) {
  return (
    <div role="group" aria-label={label} style={s.switch}>
      {options.map((o) => (
        <button key={o.id} type="button" aria-pressed={value === o.id} onClick={() => onChange(o.id)}
          style={value === o.id ? s.switchOn : s.switchOff}>{o.label}</button>
      ))}
    </div>
  );
}

function ListPanel({ title, empty, query, render, tall = false }) {
  const rows = query.data?.rows || [];
  return (
    <section style={tall ? s.panelTall : s.panel}>
      <h2 style={s.panelTitle}>{title}</h2>
      {query.isLoading && <p style={s.note}>Loading…</p>}
      {query.error && <p role="alert" style={s.error}>{query.error.message || 'This list could not be loaded.'}</p>}
      {!query.isLoading && !query.error && !rows.length && <p style={s.note}>{empty}</p>}
      {rows.map((row, i) => {
        const [main, sub, value] = render(row);
        return (
          <div key={`${main}|${sub}|${i}`} style={s.listRow}>
            <div style={{ minWidth: 0 }}>
              <div style={s.listMain}>{main}</div>
              <div style={s.listSub}>{sub}</div>
            </div>
            <div style={s.listValue}>{value}</div>
          </div>
        );
      })}
    </section>
  );
}

// Only a link into Fleetio itself is drawn as one; the API builds it from the record's key.
const FLEETIO = 'https://secure.fleetio.com/';
function FleetioLink({ url, children }) {
  if (typeof url !== 'string' || !url.startsWith(FLEETIO)) return children;
  return <a href={url} target="_blank" rel="noopener noreferrer" style={s.link}>{children}</a>;
}

const days = (v) => (v === null || v === undefined ? '–' : Math.floor(n(v)).toLocaleString('en-US'));

// The whole list behind a count, filtered as the count is, for a yard call that should not
// need Fleetio open. Each list matches its count (fleet-reporting api tests).
const DRILLS = {
  down: {
    report: 'dash-down-all', title: 'Assets down now', empty: 'No assets are down.',
    note: 'Out of Service, In Shop or At Vendor in Fleetio now. Longest down first.',
    key: (x) => `${x.fleet}|${x.unit_number}`,
    columns: [
      ['Unit', (x) => <FleetioLink url={x.fleetio_url}>{x.unit_number}</FleetioLink>],
      ['Cost center', (x) => x.yard], ['Fleet', (x) => x.fleet], ['Class', (x) => x.asset_class], ['Status', (x) => x.status],
      ['Down since', (x) => (x.down_since ? fmtDate(x.down_since) : 'No date')], ['Days down', (x) => days(x.days_down), true],
    ],
  },
  pms: {
    report: 'dash-pm-overdue-all', title: 'PMs overdue', empty: 'No PMs are overdue.',
    note: 'Overdue by date or meter. Most overdue first; by meter only comes last.',
    key: (x) => `${x.fleet}|${x.unit_number}|${x.task_name}`,
    columns: [
      ['Unit', (x) => <FleetioLink url={x.fleetio_url}>{x.unit_number}</FleetioLink>],
      ['Cost center', (x) => x.yard], ['Fleet', (x) => x.fleet], ['PM', (x) => x.task_name],
      ['Due', (x) => (x.next_due_date ? fmtDate(x.next_due_date) : 'By meter')], ['Days overdue', (x) => days(x.days_overdue), true],
      ['Meter over', (x) => (x.meter_overdue === null || x.meter_overdue === undefined ? '–' : Math.round(n(x.meter_overdue)).toLocaleString('en-US')), true],
    ],
  },
  open: {
    report: 'dash-open-work-orders', title: 'Open work orders', empty: 'No work orders are open.',
    note: 'Every open work order, oldest first.',
    key: (x) => `${x.fleet}|${x.work_order_number}`,
    columns: [
      ['Work order', (x) => <FleetioLink url={x.fleetio_url}>{x.work_order_number}</FleetioLink>],
      ['Unit', (x) => x.unit_number ?? '–'], ['Cost center', (x) => x.yard], ['Fleet', (x) => x.fleet], ['Status', (x) => x.status],
      ['Priority', (x) => x.priority_class ?? '–'], ['Where', (x) => (x.is_outside_vendor ? (x.vendor_name || 'Vendor') : 'In-house')],
      ['Opened', (x) => (x.issued_date ? fmtDate(x.issued_date) : '–')], ['Days open', (x) => days(x.open_age_days), true],
    ],
  },
};
const DRILL_LIMIT = 2000;

export function DrillList({ kind, fleet, yard, onClose }) {
  const drill = DRILLS[kind];
  const panel = useRef(null);
  useEffect(() => {
    panel.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [kind, yard]);
  const params = useMemo(() => ({ ...(fleet ? { fleet } : {}), ...(yard ? { yard } : {}) }), [fleet, yard]);
  const list = useQuery({
    queryKey: ['fleet-dashboard', 'drill', kind, params],
    queryFn: () => api.get(`/fleet-dashboard/${drill.report}`, { params }),
  });
  const rows = list.data?.rows || [];
  const linked = rows.some((x) => typeof x.fleetio_url === 'string' && x.fleetio_url.startsWith(FLEETIO));
  return (
    <section ref={panel} style={s.panel} aria-label={drill.title}>
      <div style={s.panelHead}>
        <h2 style={s.panelTitle}>{drill.title}, {yard || 'all cost centers'}{list.data ? ` (${rows.length.toLocaleString('en-US')})` : ''}</h2>
        <button type="button" onClick={onClose} style={s.closeButton}>Close list</button>
      </div>
      <p style={s.note}>
        {drill.note}{linked ? ' Choose a unit or work order to open it in Fleetio.' : ''}
        {rows.length >= DRILL_LIMIT ? ` Showing the first ${DRILL_LIMIT.toLocaleString('en-US')}; choose a cost center to narrow it.` : ''}
      </p>
      {list.isLoading && <p style={s.note}>Loading…</p>}
      {list.error && <p role="alert" style={s.error}>{list.error.message || 'This list could not be loaded.'}</p>}
      {list.data && !rows.length && <p style={s.note}>{drill.empty}</p>}
      {rows.length > 0 && (
        <div style={s.scrollTall}>
          <table style={s.table}>
            <thead><tr>{drill.columns.map(([label, , right]) => <th key={label} style={right ? s.thNum : s.th}>{label}</th>)}</tr></thead>
            <tbody>
              {rows.map((x, i) => (
                <tr key={`${drill.key(x)}|${i}`}>
                  {drill.columns.map(([label, render, right]) => <td key={label} style={right ? s.tdNum : s.td}>{render(x)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// A count that opens its list. Keeps the look of the number it replaces.
function DrillCount({ label, onOpen, children }) {
  return <button type="button" onClick={onOpen} aria-label={label} style={s.countButton}>{children}</button>;
}

function TodayTab({ grain, fleet, yard }) {
  const [drill, setDrill] = useState(null);
  const filters = useMemo(() => ({ ...(fleet ? { fleet } : {}), ...(yard ? { yard } : {}) }), [fleet, yard]);
  const today = useQuery({
    queryKey: ['fleet-dashboard', 'today', filters],
    queryFn: () => api.get('/fleet-dashboard/dash-today', { params: filters }),
  });
  const down = useQuery({
    queryKey: ['fleet-dashboard', 'down', filters],
    queryFn: () => api.get('/fleet-dashboard/dash-down', { params: filters }),
  });
  const pms = useQuery({
    queryKey: ['fleet-dashboard', 'pms', filters],
    queryFn: () => api.get('/fleet-dashboard/dash-pm-overdue', { params: filters }),
  });
  const comebacks = useQuery({
    queryKey: ['fleet-dashboard', 'comebacks', filters],
    queryFn: () => api.get('/fleet-dashboard/dash-comebacks', { params: filters }),
  });

  const rows = useMemo(() => today.data?.rows || [], [today.data]);
  // One box per cost center: a yard in both fleets adds up, and yards with no assets or work drop out.
  const yards = useMemo(() => {
    const byYard = new Map();
    for (const x of rows) {
      const e = byYard.get(x.yard) || { yard: x.yard, assets: 0, down: 0, pms: 0, open: 0, closed: 0, hours: 0 };
      e.assets += n(x.assets);
      e.down += n(x.assets_down);
      e.pms += n(x.pms_overdue);
      e.open += n(x.open_work_orders);
      e.closed += n(grain === 'DAY' ? x.closed_today : x.closed_month);
      e.hours += n(grain === 'DAY' ? x.hours_today : x.hours_month);
      byYard.set(x.yard, e);
    }
    return [...byYard.values()]
      .filter((e) => e.assets || e.open || e.closed)
      .sort((a, b) => b.down - a.down || b.pms - a.pms || a.yard.localeCompare(b.yard));
  }, [rows, grain]);

  if (today.isLoading) return <p style={s.note}>Loading today…</p>;
  if (today.error) return <p role="alert" style={s.error}>{today.error.message || 'Today could not be loaded.'}</p>;
  if (!rows.length) return <p style={s.note}>Nothing to show for this selection.</p>;

  const sum = (key) => rows.reduce((total, x) => total + n(x[key]), 0);
  const count = (v) => Math.round(v).toLocaleString('en-US');
  const daily = grain === 'DAY';
  const assets = sum('assets');
  const assetsDown = sum('assets_down');
  // Main and MRF are separate Fleetio accounts (tenants), so each tile says how the total
  // splits, unless the Fleet filter already picked one.
  const byFleet = (key) => (fleet ? null : ['Main', 'MRF']
    .map((f) => `${f} ${count(rows.filter((x) => x.fleet === f).reduce((t, x) => t + n(x[key]), 0))}`).join(' · '));
  const when = daily ? 'Today' : 'This month';
  const open = (kind, at = yard) => setDrill({ kind, yard: at });
  const closedKey = daily ? 'closed_today' : 'closed_month';
  const hoursKey = daily ? 'hours_today' : 'hours_month';

  return (
    <>
      <div style={s.tiles}>
        <Tile label="Assets down now" value={count(assetsDown)} note={`Of ${count(assets)} active assets`} split={byFleet('assets_down')} onOpen={() => open('down')} action="See every asset down" />
        <Tile label="Available now" value={pct(assets - assetsDown, assets)} note="Active assets not in a down status" split={byFleet('assets')} />
        <Tile label="PMs overdue" value={count(sum('pms_overdue'))} note={`By date or meter · PM ${pct(sum('pms_active') - sum('pms_overdue'), sum('pms_active'))} not overdue`}
          split={byFleet('pms_overdue')} onOpen={() => open('pms')} action="See every overdue PM" />
        <Tile label={`Work orders closed, ${when.toLowerCase()}`} value={count(sum(closedKey))}
          note={`${count(sum(daily ? 'opened_today' : 'opened_month'))} opened · ${count(sum('open_work_orders'))} open now`}
          split={byFleet(closedKey)} onOpen={() => open('open')} action="See open work orders" />
        <Tile label={`Shop hours, ${when.toLowerCase()}`} value={count(sum(hoursKey))}
          note="In-house, by Fleetio timer on the day worked, open jobs included" split={byFleet(hoursKey)} />
      </div>
      <p style={s.note}>
        Assets are vehicles and stationary and processing equipment, in both Fleetio accounts. Down means a status of
        Out of Service, In Shop or At Vendor. Inactive units and staging groups (Lopez Canyon) are left out.
      </p>

      <div style={s.lists}>
        <ListPanel title={`Down over 30 days${down.data ? ` (${down.data.rows.length})` : ''}`} empty="No assets have been down over 30 days." query={down} tall
          render={(x) => [`Unit ${x.unit_number}`, `${x.yard} · ${x.status}`, x.days_down === null ? 'No date' : `${Math.floor(n(x.days_down))} d`]} />
        <ListPanel title="PMs most overdue" empty="No PMs are overdue." query={pms}
          render={(x) => [`Unit ${x.unit_number}`, `${x.yard} · ${x.task_name}`, x.days_overdue === null ? 'By meter' : `${n(x.days_overdue)} d`]} />
        <ListPanel title="Came back within 30 days" empty="No come-backs in the last 30 days." query={comebacks}
          render={(x) => [`Unit ${x.unit_number}`, `${x.yard} · ${x.system_name}`, `${n(x.repeats)}×`]} />
      </div>

      {drill && <DrillList kind={drill.kind} fleet={fleet} yard={drill.yard} onClose={() => setDrill(null)} />}

      <section style={s.panel}>
        <h2 style={s.panelTitle}>Every cost center at a glance</h2>
        <p style={s.note}>Most trucks down first. {when}'s closed work and hours. Choose a count to see its list.</p>
        <div style={s.boxes}>
          {yards.map((e) => (
            <div key={e.yard} style={s.box}>
              <div style={s.boxName}>{e.yard}</div>
              <div style={s.boxRow}><span>Down</span>
                <strong><DrillCount label={`Assets down, ${e.yard}`} onOpen={() => open('down', e.yard)}>{count(e.down)} of {count(e.assets)}</DrillCount></strong></div>
              <div style={s.boxRow}><span>PMs overdue</span>
                <strong><DrillCount label={`PMs overdue, ${e.yard}`} onOpen={() => open('pms', e.yard)}>{count(e.pms)}</DrillCount></strong></div>
              <div style={s.boxRow}><span>Open work orders</span>
                <strong><DrillCount label={`Open work orders, ${e.yard}`} onOpen={() => open('open', e.yard)}>{count(e.open)}</DrillCount></strong></div>
              <div style={s.boxRow}><span>Closed</span><strong>{count(e.closed)}</strong></div>
              <div style={s.boxRow}><span>Shop hours</span><strong>{count(e.hours)}</strong></div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

const hrs = (v) => Math.round(n(v)).toLocaleString('en-US');

// Time taken as a share of standard: 100% is on standard, higher took longer.
function vsStandard(hours, standard) {
  return n(standard) > 0 ? `${Math.round((n(hours) / n(standard)) * 100)}%` : '–';
}

function MechanicTasks({ mechanic, filters, onClose }) {
  // The list of mechanics is long, so opening a person brings their tasks into view.
  const panel = useRef(null);
  useEffect(() => {
    panel.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [mechanic.technician_id, mechanic.fleet]);
  const tasks = useQuery({
    queryKey: ['fleet-dashboard', 'srt-tasks', mechanic.technician_id, mechanic.fleet, filters],
    queryFn: () => api.get('/fleet-dashboard/dash-srt-tasks', {
      params: {
        from: filters.from, to: filters.to, technician_id: mechanic.technician_id, fleet: String(mechanic.fleet).toUpperCase(),
        ...(filters.yard ? { yard: filters.yard } : {}),
      },
    }),
  });
  return (
    <section ref={panel} style={s.panel}>
      <div style={s.panelHead}>
        <h2 style={s.panelTitle}>{mechanic.technician_name}, by task</h2>
        <button type="button" onClick={onClose} style={s.closeButton}>Close</button>
      </div>
      <p style={s.note}>A task needs {TASK_MIN_JOBS} scored jobs before it shows a comparison.</p>
      {tasks.isLoading && <p style={s.note}>Loading…</p>}
      {tasks.error && <p role="alert" style={s.error}>{tasks.error.message || 'These tasks could not be loaded.'}</p>}
      {tasks.data && (
        <div style={s.scroll}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Task</th><th style={s.th}>Group</th><th style={s.th}>Standard from</th>
                <th style={s.thNum}>Jobs</th><th style={s.thNum}>Standard hours</th><th style={s.thNum}>Entered hours</th><th style={s.thNum}>Actual hours</th>
                <th style={s.thNum}>Entered vs. standard</th><th style={s.thNum}>Actual vs. standard</th>
              </tr>
            </thead>
            <tbody>
              {tasks.data.rows.map((x) => {
                const enough = n(x.scored_jobs) >= TASK_MIN_JOBS;
                return (
                  <tr key={`${x.task_name}|${x.task_group}`}>
                    <td style={s.td}>{x.task_name}</td>
                    <td style={s.td}>{x.task_group}</td>
                    <td style={s.td}>{SCORED_GROUPS.has(x.task_group) ? (x.standard_source || 'None') : 'Not scored'}</td>
                    <td style={s.tdNum}>{hrs(x.jobs)}</td>
                    <td style={s.tdNum}>{n(x.scored_standard_hours).toFixed(1)}</td>
                    <td style={s.tdNum}>{n(x.entered_hours).toFixed(1)}</td>
                    <td style={s.tdNum}>{n(x.timer_hours).toFixed(1)}</td>
                    <td style={s.tdNum}>{enough ? vsStandard(x.scored_entered_hours, x.scored_standard_hours) : '–'}</td>
                    <td style={s.tdNum}>{enough ? vsStandard(x.scored_timer_hours, x.scored_standard_hours) : '–'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ShopTab({ grain, fleet, yard, canSeeMechanics }) {
  const [picked, setPicked] = useState(null);
  const filters = useMemo(() => ({
    ...rangeFor(grain), ...(fleet ? { fleet } : {}), ...(yard ? { yard } : {}),
  }), [grain, fleet, yard]);
  const labor = useQuery({
    queryKey: ['fleet-dashboard', 'labor', filters, grain],
    queryFn: () => api.get('/fleet-dashboard/dash-labor', { params: { ...filters, grain } }),
  });
  const mechanics = useQuery({
    queryKey: ['fleet-dashboard', 'srt-mechanics', filters],
    queryFn: () => api.get('/fleet-dashboard/dash-srt-mechanics', { params: filters }),
    enabled: canSeeMechanics,
  });
  const standards = useQuery({
    queryKey: ['fleet-dashboard', 'standards', fleet],
    queryFn: () => api.get('/fleet-dashboard/dash-task-standards', { params: fleet ? { fleet } : {} }),
    staleTime: 10 * 60 * 1000,
  });

  const rows = useMemo(() => labor.data?.rows || [], [labor.data]);
  const chartData = useMemo(() => {
    const byPeriod = new Map();
    for (const x of rows) {
      if (!byPeriod.has(x.period)) byPeriod.set(x.period, { period: periodLabel(x.period, grain) });
      byPeriod.get(x.period)[x.task_group] = n(x.entered_hours);
    }
    return [...byPeriod.values()];
  }, [rows, grain]);

  if (labor.isLoading) return <p style={s.note}>Loading shop hours…</p>;
  if (labor.error) return <p role="alert" style={s.error}>{labor.error.message || 'Shop hours could not be loaded.'}</p>;
  if (!rows.length) return <p style={s.note}>No in-house labor in this period.</p>;

  const lastPeriod = rows[rows.length - 1].period;
  const latest = rows.filter((x) => x.period === lastPeriod);
  const group = (name) => n(latest.find((x) => x.task_group === name)?.entered_hours);
  const entered = latest.reduce((sum, x) => sum + n(x.entered_hours), 0);
  const timer = latest.reduce((sum, x) => sum + n(x.timer_hours), 0);
  const scored = [...SCORED_GROUPS].reduce((sum, name) => sum + group(name), 0);
  const name = periodLabel(lastPeriod, grain);
  const list = mechanics.data?.rows || [];

  return (
    <>
      <div style={s.tiles}>
        <Tile label={`Entered hours, ${name}`} value={hrs(entered)} note="In-house, on completed work orders" />
        <Tile label="Actual hours (timer)" value={hrs(timer)} note={timer > 0 ? `Entered is ${Math.round((entered / timer) * 100)}% of timer` : 'No timers'} />
        <Tile label="Repair and PM share" value={pct(scored, entered)} note="Direct, PM and bin repair" />
        <Tile label="Indirect share" value={pct(group('Indirect'), entered)} note={`${hrs(group('Indirect'))} hours, tracked but not scored`} />
        <Tile label="Road call hours" value={hrs(group('Road Call'))} note="Drive time to trucks on the road" />
      </div>

      <section style={s.panel}>
        <h2 style={s.panelTitle}>Hours by type of work</h2>
        <p style={s.note}>Entered hours, by the day a work order was completed, so the latest days fill in as open jobs finish. Task groups come from each task's name and can be corrected.</p>
        <div style={{ width: '100%', height: 280, marginTop: sp[3] }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid vertical={false} stroke={c.border} />
              <XAxis dataKey="period" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => Math.round(v).toLocaleString('en-US')} tick={{ fontSize: 12 }} width={56} />
              <Tooltip formatter={(v) => hrs(v)} />
              <Legend />
              {LABOR_GROUPS.map(([g, color]) => <Bar key={g} dataKey={g} stackId="hours" fill={color} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {!canSeeMechanics && (
        <section style={s.panel}>
          <h2 style={s.panelTitle}>Mechanics: standard, entered and actual time</h2>
          <p style={s.note}>Mechanic names need their own permission. Ask an administrator if your role needs them.</p>
        </section>
      )}

      {canSeeMechanics && picked && <MechanicTasks mechanic={picked} filters={filters} onClose={() => setPicked(null)} />}

      {canSeeMechanics && (
      <section style={s.panel}>
        <h2 style={s.panelTitle}>Mechanics: standard, entered and actual time</h2>
        <p style={s.note}>
          All three times are from Fleetio, not Workday. <strong>Entered</strong> is the hours typed on the work order,
          <strong> actual</strong> is Fleetio's timer, and <strong>standard</strong> is the task's standard time (Fleetio's, or the
          typical time from our own history where none is set). For coaching, not pay or discipline. 100% is on standard;
          higher took longer. Scored on direct, PM and bin repair only.
          Mechanics under {minHours(grain)} scored hours show no comparison. Choose a name to see that person's tasks.
        </p>
        {mechanics.isLoading && <p style={s.note}>Loading…</p>}
        {mechanics.error && <p role="alert" style={s.error}>{mechanics.error.message || 'Mechanics could not be loaded.'}</p>}
        {mechanics.data && (
          <div style={s.scrollTall}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Mechanic</th><th style={s.thNum}>Entered hours</th><th style={s.thNum}>Actual hours</th><th style={s.thNum}>Indirect</th>
                  <th style={s.thNum}>Scored jobs</th><th style={s.thNum}>Standard hours</th><th style={s.thNum}>Entered vs. standard</th><th style={s.thNum}>Actual vs. standard</th>
                  <th style={s.thNum}>Come-backs</th><th style={s.thNum}>Timers to check</th>
                </tr>
              </thead>
              <tbody>
                {list.map((x) => {
                  const enough = n(x.scored_entered_hours) >= minHours(grain);
                  return (
                    <tr key={`${x.fleet}|${x.technician_id}`}>
                      <td style={s.td}>
                        <button type="button" style={s.nameButton} aria-pressed={picked?.technician_id === x.technician_id}
                          onClick={() => setPicked(x)}>{x.technician_name}</button>
                      </td>
                      <td style={s.tdNum}>{hrs(x.entered_hours)}</td>
                      <td style={s.tdNum}>{hrs(x.timer_hours)}</td>
                      <td style={s.tdNum}>{pct(n(x.indirect_hours), n(x.entered_hours))}</td>
                      <td style={s.tdNum}>{hrs(x.scored_jobs)}</td>
                      <td style={s.tdNum}>{hrs(x.scored_standard_hours)}</td>
                      <td style={s.tdNum}>{enough ? vsStandard(x.scored_entered_hours, x.scored_standard_hours) : '–'}</td>
                      <td style={s.tdNum}>{enough ? vsStandard(x.scored_timer_hours, x.scored_standard_hours) : '–'}</td>
                      <td style={s.tdNum}>{hrs(x.comebacks)}</td>
                      <td style={s.tdNum}>{hrs(x.long_session_jobs)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}

      <section style={s.panel}>
        <h2 style={s.panelTitle}>Standards to review</h2>
        <p style={s.note}>The 100 tasks with the most hours, last 12 months. Over 25% from the standard is marked for the Fleet team.</p>
        {standards.isLoading && <p style={s.note}>Loading…</p>}
        {standards.error && <p role="alert" style={s.error}>{standards.error.message || 'Standards could not be loaded.'}</p>}
        {standards.data && (
          <div style={s.scrollTall}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Task</th><th style={s.th}>Group</th><th style={s.th}>Fleet</th><th style={s.thNum}>Jobs</th><th style={s.thNum}>Hours</th>
                  <th style={s.thNum}>Standard</th><th style={s.thNum}>Typical entered</th><th style={s.thNum}>Typical actual</th><th style={s.th}>Review</th>
                </tr>
              </thead>
              <tbody>
                {standards.data.rows.map((x) => {
                  const std = n(x.standard_hours);
                  const typical = n(x.typical_timer_hours);
                  const scoredTask = SCORED_GROUPS.has(x.task_group);
                  let review = '';
                  if (scoredTask && !std) review = 'No standard';
                  else if (scoredTask && typical > std * 1.25) review = 'Standard looks low';
                  else if (scoredTask && typical && typical < std * 0.75) review = 'Standard looks high';
                  return (
                    <tr key={`${x.fleet}|${x.task_name}`}>
                      <td style={s.td}>{x.task_name}</td>
                      <td style={s.td}>{x.task_group}</td>
                      <td style={s.td}>{x.fleet}</td>
                      <td style={s.tdNum}>{hrs(x.jobs)}</td>
                      <td style={s.tdNum}>{hrs(x.total_entered_hours)}</td>
                      <td style={s.tdNum}>{std ? std.toFixed(2) : '–'}</td>
                      <td style={s.tdNum}>{n(x.typical_entered_hours).toFixed(2)}</td>
                      <td style={s.tdNum}>{typical ? typical.toFixed(2) : '–'}</td>
                      <td style={s.td}>{review}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function TrucksTab({ grain, fleet, yard }) {
  const [assetClass, setAssetClass] = useState('');
  const where = useMemo(() => ({ ...(fleet ? { fleet } : {}), ...(yard ? { yard } : {}) }), [fleet, yard]);
  const months = useMemo(() => ({ ...rangeFor('MONTH'), ...where }), [where]);
  const days = useMemo(() => ({ ...rangeFor('DAY'), ...where }), [where]);
  const reliability = useQuery({
    queryKey: ['fleet-dashboard', 'reliability', months],
    queryFn: () => api.get('/fleet-dashboard/dash-reliability', { params: months }),
  });
  const daily = useQuery({
    queryKey: ['fleet-dashboard', 'uptime-daily', days],
    queryFn: () => api.get('/fleet-dashboard/fleet-uptime-daily', { params: days }),
    enabled: grain === 'DAY',
  });
  const status = useQuery({
    queryKey: ['fleet-dashboard', 'truck-status', where],
    queryFn: () => api.get('/fleet-dashboard/dash-truck-status', { params: where }),
  });
  const worst = useQuery({
    queryKey: ['fleet-dashboard', 'trucks-worst', where],
    queryFn: () => api.get('/fleet-dashboard/dash-trucks-worst', { params: where }),
  });
  const meterGaps = useQuery({
    queryKey: ['fleet-dashboard', 'meter-gaps', where],
    queryFn: () => api.get('/fleet-dashboard/dash-meter-gaps', { params: where }),
  });
  const lifecycle = useQuery({
    queryKey: ['fleet-dashboard', 'lifecycle'],
    queryFn: () => api.get('/fleet-dashboard/dash-lifecycle'),
    staleTime: 10 * 60 * 1000,
  });

  const thisMonth = `${pacificToday().slice(0, 7)}-01`;
  const full = useMemo(
    () => (reliability.data?.rows || []).filter((x) => String(x.month).slice(0, 10) < thisMonth),
    [reliability.data, thisMonth],
  );
  const monthChart = useMemo(() => full.map((x) => ({
    period: periodLabel(x.month, 'MONTH'),
    Available: n(x.truck_days) > 0 ? Math.round((1 - n(x.days_down) / n(x.truck_days)) * 1000) / 10 : null,
    'Unplanned jobs': n(x.unplanned_jobs),
    'Road calls': n(x.road_calls),
  })), [full]);
  const dayChart = useMemo(() => {
    const byDay = new Map();
    for (const x of daily.data?.rows || []) {
      const e = byDay.get(x.snapshot_date) || { period: periodLabel(x.snapshot_date, 'DAY'), units: 0, down: 0 };
      e.units += n(x.units);
      e.down += n(x.down_units);
      byDay.set(x.snapshot_date, e);
    }
    return [...byDay.values()].map((e) => ({ period: e.period, 'Trucks down': e.down, units: e.units }));
  }, [daily.data]);
  const classes = useMemo(() => [...new Set((lifecycle.data?.rows || []).map((x) => x.asset_class))], [lifecycle.data]);
  const pickedClass = assetClass || classes[0] || '';
  const ages = (lifecycle.data?.rows || []).filter((x) => x.asset_class === pickedClass);

  if (reliability.isLoading) return <p style={s.note}>Loading trucks…</p>;
  if (reliability.error) return <p role="alert" style={s.error}>{reliability.error.message || 'Truck figures could not be loaded.'}</p>;

  const last = full[full.length - 1];
  const prior = full[full.length - 2];
  const when = last ? periodLabel(last.month, 'MONTH') : '';
  const availability = (x) => (x && n(x.truck_days) > 0 ? `${((1 - n(x.days_down) / n(x.truck_days)) * 100).toFixed(1)}%` : '–');
  const milesPer = (x, count) => (x && n(count) > 0 ? Math.round(n(x.miles) / n(count)).toLocaleString('en-US') : '–');
  const st = status.data?.rows?.[0];
  const latestDay = dayChart[dayChart.length - 1];
  const showDays = grain === 'DAY';

  return (
    <>
      <div style={s.tiles}>
        {showDays
          ? <Tile label={`Trucks down, ${latestDay?.period || 'today'}`} value={latestDay ? hrs(latestDay['Trucks down']) : '–'} note={latestDay ? `Of ${hrs(latestDay.units)} vehicles` : 'No daily record yet'} />
          : <Tile label={`Available, ${when}`} value={availability(last)} note={prior ? `${periodLabel(prior.month, 'MONTH')}: ${availability(prior)}` : 'No earlier month'} />}
        <Tile label="Down days per truck" value={last && n(last.trucks) > 0 ? (n(last.days_down) / n(last.trucks)).toFixed(1) : '–'} note={when ? `${when}, ${hrs(last.trucks)} active assets` : ''} />
        <Tile label="Miles between road calls" value={milesPer(last, last?.road_calls_on_miles)} note={`${when}. An unplanned repair every ${milesPer(last, last?.unplanned_jobs_on_miles)} miles`} />
        <Tile label="Road calls" value={last ? hrs(last.road_calls) : '–'} note={prior ? `${periodLabel(prior.month, 'MONTH')}: ${hrs(prior.road_calls)}` : when} />
        <Tile label="Faults with no repair since" value={st ? hrs(st.faults_no_repair) : '–'} note={st ? `${hrs(st.underused_trucks)} of ${hrs(st.judged_trucks)} judged trucks underused` : ''} />
      </div>

      <div style={s.lists}>
        <section style={s.panel}>
          <h2 style={s.panelTitle}>{showDays ? 'Trucks down by day' : 'Availability by month'}</h2>
          <p style={s.note}>{showDays ? 'From the daily record, which starts 10/30/2026.' : 'Full months only. Truck-days not in a down status.'}</p>
          <div style={{ width: '100%', height: 240, marginTop: sp[2] }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={showDays ? dayChart : monthChart}>
                <CartesianGrid vertical={false} stroke={c.border} />
                <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} width={40} domain={showDays ? [0, 'auto'] : [0, 100]} />
                <Tooltip />
                <Bar dataKey={showDays ? 'Trucks down' : 'Available'} fill={showDays ? UNPLANNED : PLANNED} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
        <section style={s.panel}>
          <h2 style={s.panelTitle}>Unplanned jobs and road calls</h2>
          <p style={s.note}>Full months only.</p>
          <div style={{ width: '100%', height: 240, marginTop: sp[2] }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthChart}>
                <CartesianGrid vertical={false} stroke={c.border} />
                <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} width={48} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Unplanned jobs" fill={UNPLANNED} />
                <Bar dataKey="Road calls" fill="#7A5AA6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>

      <section style={s.panel}>
        <h2 style={s.panelTitle}>Worst trucks by unplanned cost</h2>
        <p style={s.note}>Last 12 full months, active vehicles.</p>
        {worst.error && <p role="alert" style={s.error}>{worst.error.message || 'This list could not be loaded.'}</p>}
        {worst.data && (
          <div style={s.scroll}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Unit</th><th style={s.th}>Cost center</th><th style={s.th}>Class</th><th style={s.th}>Make and model</th><th style={s.thNum}>Year</th>
                  <th style={s.thNum}>Unplanned cost</th><th style={s.thNum}>All R&amp;M</th><th style={s.thNum}>Down days</th><th style={s.thNum}>Unplanned jobs</th>
                  <th style={s.thNum}>Road calls</th><th style={s.thNum}>Engine hours</th><th style={s.thNum}>Miles between unplanned repairs</th>
                </tr>
              </thead>
              <tbody>
                {worst.data.rows.map((x) => (
                  <tr key={`${x.fleet}|${x.unit_number}`}>
                    <td style={s.td}>{x.unit_number}</td>
                    <td style={s.td}>{x.yard}</td>
                    <td style={s.td}>{x.asset_class}</td>
                    <td style={s.td}>{[x.make, x.model].filter(Boolean).join(' ')}</td>
                    <td style={s.tdNum}>{x.model_year ?? '–'}</td>
                    <td style={s.tdTotal}>{fmtMoney(n(x.unplanned_cost))}</td>
                    <td style={s.tdNum}>{fmtMoney(n(x.rm_cost))}</td>
                    <td style={s.tdNum}>{Math.round(n(x.days_down))}</td>
                    <td style={s.tdNum}>{hrs(x.unplanned_jobs)}</td>
                    <td style={s.tdNum}>{hrs(x.road_calls)}</td>
                    <td style={s.tdNum}>{x.engine_hours === null ? '–' : hrs(x.engine_hours)}</td>
                    <td style={s.tdNum}>{x.miles_between_unplanned_repairs === null ? '–' : hrs(x.miles_between_unplanned_repairs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section style={s.panel}>
        <h2 style={s.panelTitle}>Meters not reporting</h2>
        <p style={s.note}>
          Assets that should report through Samsara and have not in the last 7 days. Quiet means 8-30 days,
          Silent means over 30, and Never means no reading at all. Longest first.
        </p>
        {meterGaps.error && <p role="alert" style={s.error}>{meterGaps.error.message || 'This list could not be loaded.'}</p>}
        {meterGaps.data && (
          <div style={s.scrollTall}>
            <table style={s.table}>
              <thead><tr><th style={s.th}>Unit</th><th style={s.th}>Cost center</th><th style={s.th}>Class</th><th style={s.th}>Status</th><th style={s.th}>Last reading</th><th style={s.thNum}>Days</th></tr></thead>
              <tbody>
                {meterGaps.data.rows.map((x) => (
                  <tr key={`${x.fleet}|${x.unit_number}`}>
                    <td style={s.td}>{x.unit_number}</td>
                    <td style={s.td}>{x.yard}</td>
                    <td style={s.td}>{x.asset_class}</td>
                    <td style={s.td}>{x.reporting_status}</td>
                    <td style={s.td}>{x.last_reading_date ? fmtDate(x.last_reading_date) : 'Never'}</td>
                    <td style={s.tdNum}>{x.days_since_last_reading ?? '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section style={s.panel}>
        <div style={s.panelHead}>
          <h2 style={s.panelTitle}>Keep or replace: cost by truck age</h2>
          <label style={s.inlineLabel}>Asset class
            <select value={pickedClass} onChange={(e) => setAssetClass(e.target.value)} style={s.selectLight}>
              {classes.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </label>
        </div>
        <p style={s.note}>All history, company-wide, so a yard's filter does not change this. Ages with under 5 truck-years are left out. Rising cost shows when a class costs more to keep.</p>
        {lifecycle.error && <p role="alert" style={s.error}>{lifecycle.error.message || 'This view could not be loaded.'}</p>}
        <div style={s.scroll}>
          <table style={s.table}>
            <thead><tr><th style={s.th}>Age (years)</th><th style={s.thNum}>Truck-years</th><th style={s.thNum}>Cost per truck-year</th><th style={s.thNum}>Down days per truck-year</th></tr></thead>
            <tbody>
              {ages.map((x) => (
                <tr key={x.age_year}>
                  <td style={s.td}>{x.age_year}</td>
                  <td style={s.tdNum}>{hrs(x.truck_years)}</td>
                  <td style={s.tdNum}>{fmtMoney(n(x.rm_cost_per_truck_year))}</td>
                  <td style={s.tdNum}>{n(x.days_down_per_truck_year).toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

// A ratio that stays blank rather than dividing by nothing.
const ratio = (a, b) => (n(b) > 0 ? n(a) / n(b) : null);
const money = (v) => (v === null ? '–' : fmtMoney(v));
const percent = (v) => (v === null ? '–' : `${(v * 100).toFixed(1)}%`);
const whole = (v) => (v === null ? '–' : Math.round(v).toLocaleString('en-US'));
const oneDp = (v) => (v === null ? '–' : v.toFixed(1));

// Each measure names which way is better, so a column ranks the right way round.
const LEAGUE_SETS = {
  reliability: { label: 'Truck Reliability', columns: [
    ['Available', (x) => (n(x.truck_days) > 0 ? 1 - n(x.days_down) / n(x.truck_days) : null), percent, true],
    ['Down days per truck', (x) => ratio(x.days_down, x.trucks), oneDp, false],
    ['Miles between road calls', (x) => ratio(x.miles, x.road_calls_on_miles), whole, true],
    ['Miles between unplanned repairs', (x) => ratio(x.miles, x.unplanned_jobs_on_miles), whole, true],
    ['Road calls per truck', (x) => ratio(x.road_calls, x.trucks), oneDp, false],
  ] },
  cost: { label: 'Costs', columns: [
    ['Cost per truck', (x) => ratio(x.vehicle_rm_cost, x.trucks), money, false],
    ['Cost per mile', (x) => ratio(x.rm_cost_on_miles, x.miles), (v) => (v === null ? '–' : `$${v.toFixed(2)}`), false],
    ['Cost per engine hour', (x) => ratio(x.rm_cost_on_hours, x.engine_hours), (v) => (v === null ? '–' : `$${v.toFixed(2)}`), false],
    ['Unplanned share', (x) => ratio(x.unplanned_cost, x.total_cost), percent, false],
    ['Vendor share', (x) => ratio(x.vendor_cost, x.total_cost), percent, false],
  ] },
  shop: { label: 'Mechanics & Shop', columns: [
    ['Entered vs. standard', (x) => ratio(x.scored_entered_hours, x.standard_hours), percent, false],
    ['Actual vs. standard', (x) => ratio(x.scored_timer_hours, x.standard_hours), percent, false],
    ['Indirect share', (x) => ratio(x.indirect_hours, x.entered_hours), percent, false],
    ['Come-backs per 100 jobs', (x) => { const r = ratio(x.comebacks, x.jobs); return r === null ? null : r * 100; }, oneDp, false],
  ] },
  quality: { label: 'Data Quality', columns: [
    ['Priority', (x) => (x.pct_priority_class === null ? null : n(x.pct_priority_class) / 100), percent, true],
    ['Repair code', (x) => (x.pct_lines_repair_coded === null ? null : n(x.pct_lines_repair_coded) / 100), percent, true],
    ['Reason', (x) => (x.pct_lines_reason === null ? null : n(x.pct_lines_reason) / 100), percent, true],
    ['Technician', (x) => (x.pct_technician === null ? null : n(x.pct_technician) / 100), percent, true],
    ['Hours', (x) => (x.pct_labor_hours === null ? null : n(x.pct_labor_hours) / 100), percent, true],
  ] },
};
// A yard this small moves a whole band on one truck, so it is shown but not ranked.
const LEAGUE_MIN_VEHICLES = 10;
// The fifth entry names the list a count opens (DRILLS).
const TODAY_COLUMNS = [
  ['Available now', (x) => (x.assets > 0 ? 1 - x.down / x.assets : null), percent, true],
  ['Assets down', (x) => (x.assets > 0 ? x.down : null), whole, false, 'down'],
  ['PMs overdue', (x) => x.pms, whole, false, 'pms'],
  ['PM %', (x) => ratio(x.pmsActive - x.pms, x.pmsActive), percent, true],
  ['Open work orders', (x) => x.open, whole, false, 'open'],
];

function shade(rank, count) {
  const place = count > 1 ? rank / (count - 1) : 0;
  if (place < 0.2) return { background: '#1F4E9E', color: '#FFFFFF' };
  if (place < 0.45) return { background: '#DCE7F8', color: '#14305F' };
  if (place < 0.65) return { background: c.canvas, color: c.textStrong };
  if (place < 0.85) return { background: '#F8DEC8', color: '#6A2F07' };
  return { background: '#C65A12', color: '#FFFFFF' };
}

function YardsTab({ grain, fleet, yard }) {
  const [set, setSet] = useState('reliability');
  const [drill, setDrill] = useState(null);
  const daily = grain === 'DAY';
  const today = pacificToday();
  const lastFull = useMemo(() => {
    const d = new Date(`${today.slice(0, 7)}-01T00:00:00Z`);
    d.setUTCMonth(d.getUTCMonth() - 1);
    return d.toISOString().slice(0, 10);
  }, [today]);
  const league = useQuery({
    queryKey: ['fleet-dashboard', 'league', lastFull, fleet],
    queryFn: () => api.get('/fleet-dashboard/dash-yard-league', { params: { month: lastFull, ...(fleet ? { fleet } : {}) } }),
    enabled: !daily,
  });
  const now = useQuery({
    queryKey: ['fleet-dashboard', 'today', fleet ? { fleet } : {}],
    queryFn: () => api.get('/fleet-dashboard/dash-today', { params: fleet ? { fleet } : {} }),
    enabled: daily,
  });

  const rows = useMemo(() => {
    if (!daily) return (league.data?.rows || []).filter((x) => n(x.trucks) > 0 || n(x.work_orders) > 0);
    const byYard = new Map();
    for (const x of now.data?.rows || []) {
      const e = byYard.get(x.yard) || { yard: x.yard, assets: 0, mrfAssets: 0, down: 0, pms: 0, pmsActive: 0, open: 0 };
      e.assets += n(x.assets);
      if (x.fleet === 'MRF') e.mrfAssets += n(x.assets);
      e.down += n(x.assets_down);
      e.pms += n(x.pms_overdue);
      e.pmsActive += n(x.pms_active);
      e.open += n(x.open_work_orders);
      byYard.set(x.yard, e);
    }
    return [...byYard.values()].filter((e) => e.assets > 0 || e.open > 0);
  }, [daily, league.data, now.data]);

  const columns = daily ? TODAY_COLUMNS : LEAGUE_SETS[set].columns;
  const vehiclesOf = (x) => (daily ? x.assets : n(x.trucks));
  // How a yard's assets split between the two Fleetio accounts (tenants).
  const tenants = (x) => {
    const mrf = daily ? x.mrfAssets : n(x.mrf_trucks);
    return `${whole(vehiclesOf(x) - mrf)} · ${whole(mrf)}`;
  };
  const ranked = useMemo(() => columns.map(([, value, , higherIsBetter]) => {
    const values = rows.filter((x) => (daily ? x.assets : n(x.trucks)) >= LEAGUE_MIN_VEHICLES).map(value).filter((v) => v !== null);
    return [...values].sort((a, b) => (higherIsBetter ? b - a : a - b));
  }), [columns, rows, daily]);
  const query = daily ? now : league;

  return (
    <>
    <section style={s.panel}>
      <div style={s.panelHead}>
        <h2 style={s.panelTitle}>{daily ? 'Every cost center right now' : `Every cost center, ${periodLabel(lastFull, 'MONTH')}`}</h2>
        {!daily && <Switch options={Object.entries(LEAGUE_SETS).map(([id, x]) => ({ id, label: x.label }))} value={set} onChange={setSet} label="Measures" />}
      </div>
      <p style={s.note}>
        Shading ranks each column: blue is better, orange is worse. Yards under {LEAGUE_MIN_VEHICLES} assets are shown but not ranked. {daily ? 'Choose a count to see its list. PM % is active PM reminders not overdue. Monthly shows cost, shop and reliability measures for the last full month.'
          : 'Data quality is how completely work orders carry a priority, repair code, reason, technician and hours, and how many assets at the yard are reporting meters through Samsara. Read a ranking beside it: poor data can move a yard either way.'}
      </p>
      <div style={s.legend} aria-label="Color legend">
        {[['Best fifth', 0], ['Better', 0.3], ['Middle', 0.55], ['Worse', 0.75], ['Worst fifth', 0.95]].map(([label, place]) => (
          <span key={label} style={s.legendItem}>
            <span style={{ ...s.legendSwatch, ...shade(Math.round(place * 100), 101) }} />{label}
          </span>
        ))}
        <span style={s.legendItem}><span style={{ ...s.legendSwatch, background: c.surface, border: `1px solid ${c.border}` }} />Not ranked</span>
      </div>
      {query.isLoading && <p style={s.note}>Loading…</p>}
      {query.error && <p role="alert" style={s.error}>{query.error.message || 'Yards could not be loaded.'}</p>}
      {query.data && (
        <div style={s.scroll}>
          <table style={s.league}>
            <thead>
              <tr>
                <th style={s.th}>Cost center</th>
                <th style={s.thNum}>{daily ? 'Total assets' : 'Assets active'}</th>
                {!fleet && <th style={s.thNum}>Main · MRF</th>}
                {columns.map(([label]) => <th key={label} style={s.thCenter}>{label}</th>)}
                {!daily && <th style={s.thCenter}>Meters reporting</th>}
                {!daily && <th style={s.thCenter}>Data quality</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.yard} style={yard && x.yard.toUpperCase() === yard.toUpperCase() ? s.leagueOn : undefined}>
                  <td style={s.td}>{x.yard}</td>
                  <td style={s.tdNum}>{whole(vehiclesOf(x))}</td>
                  {!fleet && <td style={s.tdNum}>{tenants(x)}</td>}
                  {columns.map(([label, value, format, , kind], i) => {
                    const v = value(x);
                    const shown = format(v);
                    return (
                      <td key={label} style={{ ...s.leagueCell, ...(v === null || vehiclesOf(x) < LEAGUE_MIN_VEHICLES ? {} : shade(ranked[i].indexOf(v), ranked[i].length)) }}>
                        {kind && v ? <DrillCount label={`${label}, ${x.yard}`} onOpen={() => setDrill({ kind, yard: x.yard })}>{shown}</DrillCount> : shown}
                      </td>
                    );
                  })}
                  {!daily && <td style={s.tdCenter}>{n(x.meter_assets) > 0 ? `${whole(n(x.meter_reporting))} of ${whole(n(x.meter_assets))}` : '–'}</td>}
                  {!daily && <td style={s.tdCenter}>{x.data_quality_score === null ? '–' : `${Math.round(n(x.data_quality_score))}%`}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
    {daily && drill && <DrillList kind={drill.kind} fleet={fleet} yard={drill.yard} onClose={() => setDrill(null)} />}
    {!daily && set === 'quality' && <QualityDetail fleet={fleet} yard={yard} />}
    </>
  );
}

function QualityDetail({ fleet, yard }) {
  const where = useMemo(() => ({ ...(fleet ? { fleet } : {}), ...(yard ? { yard } : {}) }), [fleet, yard]);
  const trend = useQuery({
    queryKey: ['fleet-dashboard', 'quality-trend', where],
    queryFn: () => api.get('/fleet-dashboard/dash-quality-trend', { params: { ...rangeFor('MONTH'), ...where } }),
  });
  const gaps = useQuery({
    queryKey: ['fleet-dashboard', 'wo-gaps', where],
    queryFn: () => api.get('/fleet-dashboard/dash-wo-gaps', { params: where }),
  });
  const chartData = useMemo(() => (trend.data?.rows || []).map((x) => ({
    period: periodLabel(x.month, 'MONTH'), 'Data quality': n(x.data_quality_score),
  })), [trend.data]);
  const missing = (x) => [
    x.no_priority && 'Priority',
    n(x.lines_no_repair_code) > 0 && `Repair code (${n(x.lines_no_repair_code)} lines)`,
    n(x.lines_no_reason) > 0 && `Reason (${n(x.lines_no_reason)} lines)`,
    x.no_technician && 'Technician',
    x.no_hours && 'Hours',
  ].filter(Boolean).join(', ');

  return (
    <>
      <section style={s.panel}>
        <h2 style={s.panelTitle}>Data quality by month</h2>
        <p style={s.note}>{yard || 'All cost centers'}. Work order items are by month; meters reporting is as of today.</p>
        {trend.error && <p role="alert" style={s.error}>{trend.error.message || 'The trend could not be loaded.'}</p>}
        <div style={{ width: '100%', height: 220, marginTop: sp[2] }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid vertical={false} stroke={c.border} />
              <XAxis dataKey="period" tick={{ fontSize: 12 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} width={40} />
              <Tooltip />
              <Bar dataKey="Data quality" fill={PLANNED} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>
      <section style={s.panel}>
        <h2 style={s.panelTitle}>Work orders to correct</h2>
        <p style={s.note}>Completed in the last 60 days and missing something. Newest 200. Choose a cost center above to narrow this.</p>
        {gaps.error && <p role="alert" style={s.error}>{gaps.error.message || 'This list could not be loaded.'}</p>}
        {gaps.data && (
          <div style={s.scrollTall}>
            <table style={s.table}>
              <thead><tr><th style={s.th}>Work order</th><th style={s.th}>Unit</th><th style={s.th}>Cost center</th><th style={s.th}>Completed</th><th style={s.th}>Missing</th></tr></thead>
              <tbody>
                {gaps.data.rows.map((x) => (
                  <tr key={`${x.fleet}|${x.work_order_number}`}>
                    <td style={s.td}>{x.work_order_number}</td>
                    <td style={s.td}>{x.unit_number ?? '–'}</td>
                    <td style={s.td}>{x.yard}</td>
                    <td style={s.td}>{fmtDate(x.completed_date)}</td>
                    <td style={s.td}>{missing(x)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function PartsTab({ grain, fleet }) {
  const where = useMemo(() => (fleet ? { fleet } : {}), [fleet]);
  const stock = useQuery({
    queryKey: ['fleet-dashboard', 'parts', where],
    queryFn: () => api.get('/fleet-dashboard/dash-parts', { params: where }),
  });
  const pos = useQuery({
    queryKey: ['fleet-dashboard', 'parts-pos', where],
    queryFn: () => api.get('/fleet-dashboard/dash-parts-pos', { params: where }),
  });
  const filters = useMemo(() => ({ ...rangeFor(grain), grain, by: 'TOTAL', ...where }), [grain, where]);
  const issued = useQuery({
    queryKey: ['fleet-dashboard', 'cost', filters, 'parts'],
    queryFn: () => api.get('/fleet-dashboard/dash-cost', { params: filters }),
  });
  const chartData = useMemo(() => (issued.data?.rows || []).map((x) => ({
    period: periodLabel(x.period, grain), 'Parts on in-house work': n(x.in_house_parts),
  })), [issued.data, grain]);

  if (stock.isLoading) return <p style={s.note}>Loading parts…</p>;
  if (stock.error) return <p role="alert" style={s.error}>{stock.error.message || 'Parts could not be loaded.'}</p>;
  const rows = stock.data.rows;
  const sum = (key) => rows.reduce((total, x) => total + n(x[key]), 0);
  const po = pos.data?.rows?.[0];

  return (
    <>
      <div style={s.tiles}>
        <Tile label="Inventory value" value={fmtMoney(sum('inventory_value'))} note={`${whole(sum('lines'))} lines with value`} />
        <Tile label="Aged +12 months" value={fmtMoney(sum('aged_12'))} note="Excludes critical spares" />
        <Tile label="Critical Spare +12 months" value={fmtMoney(sum('critical_12'))} note="Tracked apart from aged stock" />
        <Tile label="Critical spares below min" value={whole(sum('critical_below_min'))} note={`${fmtMoney(sum('critical_short'))} short`} />
        <Tile label="Open purchase orders" value={po ? fmtMoney(n(po.open_value)) : '–'} note={po ? `${whole(n(po.open_pos))} open, oldest ${whole(n(po.oldest_days))} days` : ''} />
      </div>
      <p style={s.warning}>The aging rules are still being compared with the old Inventory page, so treat aged figures as provisional. Stock locations are Fleetio's, so the cost center filter does not apply on this tab.</p>
      <section style={s.panel}>
        <h2 style={s.panelTitle}>Parts used on in-house work</h2>
        <p style={s.note}>By completion date.</p>
        <div style={{ width: '100%', height: 240, marginTop: sp[2] }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid vertical={false} stroke={c.border} />
              <XAxis dataKey="period" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => (v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : `$${Math.round(v / 1000)}k`)} tick={{ fontSize: 12 }} width={56} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Bar dataKey="Parts on in-house work" fill={PLANNED} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>
      <section style={s.panel}>
        <h2 style={s.panelTitle}>Stock by location</h2>
        <div style={s.scroll}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Location</th><th style={s.th}>Fleet</th><th style={s.thNum}>Value</th><th style={s.thNum}>Lines</th><th style={s.thNum}>Aged 6-12</th><th style={s.thNum}>Aged +12</th>
                <th style={s.thNum}>Critical +12</th><th style={s.thNum}>Non-Stock +45</th><th style={s.thNum}>Over max</th><th style={s.thNum}>Critical below min</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={`${x.fleet}|${x.location_name}`}>
                  <td style={s.td}>{x.location_name}</td>
                  <td style={s.td}>{x.fleet}</td>
                  <td style={s.tdTotal}>{fmtMoney(n(x.inventory_value))}</td>
                  <td style={s.tdNum}>{whole(n(x.lines))}</td>
                  <td style={s.tdNum}>{fmtMoney(n(x.aged_6_12))}</td>
                  <td style={s.tdNum}>{fmtMoney(n(x.aged_12))}</td>
                  <td style={s.tdNum}>{fmtMoney(n(x.critical_12))}</td>
                  <td style={s.tdNum}>{fmtMoney(n(x.non_stock_45))}</td>
                  <td style={s.tdNum}>{fmtMoney(n(x.over_max))}</td>
                  <td style={s.tdNum}>{whole(n(x.critical_below_min))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

// The first day of PM reminder history (fleet-reporting pm_reminder_snapshot): Fleetio keeps
// only the current state, so nothing earlier can be shown.
export const PM_HISTORY_START = '2026-10-26';

function ComplianceTab({ grain, fleet, yard }) {
  const [drill, setDrill] = useState(null);
  const where = useMemo(() => ({ ...(fleet ? { fleet } : {}), ...(yard ? { yard } : {}) }), [fleet, yard]);
  const trend = useQuery({
    queryKey: ['fleet-dashboard', 'pm-trend', where],
    queryFn: () => api.get('/fleet-dashboard/dash-pm-compliance-trend', { params: { from: PM_HISTORY_START, to: pacificToday(), ...where } }),
  });
  const trendData = useMemo(() => (trend.data?.rows || []).map((x) => ({
    period: periodLabel(x.day, 'DAY'),
    'PM %': n(x.pms_active) > 0 ? Math.round((1 - n(x.pms_overdue) / n(x.pms_active)) * 1000) / 10 : null,
  })), [trend.data]);
  const status = useQuery({
    queryKey: ['fleet-dashboard', 'compliance', where],
    queryFn: () => api.get('/fleet-dashboard/dash-compliance', { params: where }),
  });
  const done = useQuery({
    queryKey: ['fleet-dashboard', 'pm-done', where, grain],
    queryFn: () => api.get('/fleet-dashboard/dash-pm-done', { params: { ...rangeFor(grain), grain, ...where } }),
  });
  const pms = useQuery({
    queryKey: ['fleet-dashboard', 'pms', where],
    queryFn: () => api.get('/fleet-dashboard/dash-pm-overdue', { params: where }),
  });
  const renewals = useQuery({
    queryKey: ['fleet-dashboard', 'renewals', where],
    queryFn: () => api.get('/fleet-dashboard/dash-renewals', { params: where }),
  });
  const chartData = useMemo(() => (done.data?.rows || []).map((x) => ({
    period: periodLabel(x.period, grain), 'PM jobs completed': n(x.pm_jobs),
  })), [done.data, grain]);

  if (status.isLoading) return <p style={s.note}>Loading PM and compliance…</p>;
  if (status.error) return <p role="alert" style={s.error}>{status.error.message || 'PM and compliance could not be loaded.'}</p>;
  const rows = status.data.rows;
  const sum = (key) => rows.reduce((total, x) => total + n(x[key]), 0);
  const byYard = new Map();
  for (const x of rows) {
    const e = byYard.get(x.yard) || { yard: x.yard, pms_active: 0, pms_overdue: 0, renewals_overdue: 0, renewals_due_soon: 0, inspections_month: 0, inspections_with_defects_month: 0, open_inspection_issues: 0 };
    for (const key of Object.keys(e)) if (key !== 'yard') e[key] += n(x[key]);
    byYard.set(x.yard, e);
  }
  const yards = [...byYard.values()].sort((a, b) => b.pms_overdue - a.pms_overdue);
  const lastDone = chartData[chartData.length - 1];

  return (
    <>
      <div style={s.tiles}>
        <Tile label="PM %" value={pct(sum('pms_active') - sum('pms_overdue'), sum('pms_active'))} note={`${pct(sum('pms_active') - sum('pms_overdue'), sum('pms_active'))} of ${whole(sum('pms_active'))} active reminders not overdue`} />
        <Tile label="PMs overdue" value={whole(sum('pms_overdue'))} note="By date or meter" onOpen={() => setDrill({ kind: 'pms', yard })} action="See every overdue PM" />
        <Tile label={lastDone ? `PM jobs completed, ${lastDone.period}` : 'PM jobs completed'} value={lastDone ? whole(lastDone['PM jobs completed']) : '–'} note="Repair jobs with a PM task" />
        <Tile label="Inspections this month" value={whole(sum('inspections_month'))} note={`${pct(sum('inspections_with_defects_month'), sum('inspections_month'))} found defects · last month ${whole(sum('inspections_prior_month'))}`} />
        <Tile label="Open inspection issues" value={whole(sum('open_inspection_issues'))} note="Raised by an inspection, not yet resolved" />
        <Tile label="Renewals overdue" value={whole(sum('renewals_overdue'))} note={`${whole(sum('renewals_due_soon'))} more due soon`} />
      </div>
      {drill && <DrillList kind={drill.kind} fleet={fleet} yard={drill.yard} onClose={() => setDrill(null)} />}
      <section style={s.panel}>
        <h2 style={s.panelTitle}>PM % by day</h2>
        <p style={s.note}>
          Active PM reminders not overdue at the end of each day. History starts {fmtDate(PM_HISTORY_START)}, when it began being
          recorded, so the trend grows from there. A day can read slightly better than today's live figure where a PM is overdue
          by meter before Fleetio marks it.
        </p>
        {trend.error && <p role="alert" style={s.error}>{trend.error.message || 'The trend could not be loaded.'}</p>}
        <div style={{ width: '100%', height: 220, marginTop: sp[2] }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trendData}>
              <CartesianGrid vertical={false} stroke={c.border} />
              <XAxis dataKey="period" tick={{ fontSize: 12 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} width={40} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Bar dataKey="PM %" fill={PLANNED} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>
      <section style={s.panel}>
        <h2 style={s.panelTitle}>PM jobs completed</h2>
        <p style={s.note}>By completion date.</p>
        <div style={{ width: '100%', height: 240, marginTop: sp[2] }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid vertical={false} stroke={c.border} />
              <XAxis dataKey="period" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} width={48} />
              <Tooltip />
              <Bar dataKey="PM jobs completed" fill={PLANNED} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>
      <div style={s.lists}>
        <ListPanel title="PMs most overdue" empty="No PMs are overdue." query={pms}
          render={(x) => [`Unit ${x.unit_number}`, `${x.yard} · ${x.task_name}`, x.days_overdue === null ? 'By meter' : `${n(x.days_overdue)} d`]} />
        <ListPanel title="Renewals overdue or due soon" empty="No renewals are overdue or due soon." query={renewals}
          render={(x) => [`Unit ${x.unit_number}`, `${x.yard} · ${x.renewal_type}`, x.is_overdue ? `${Math.abs(n(x.days_until_due))} d late` : `${n(x.days_until_due)} d`]} />
      </div>
      <section style={s.panel}>
        <h2 style={s.panelTitle}>By cost center</h2>
        <p style={s.note}>Most PMs overdue first. PM % is active PM reminders not overdue. Choose a count to see its list.</p>
        <div style={s.scroll}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Cost center</th><th style={s.thNum}>PM %</th><th style={s.thNum}>PMs overdue</th><th style={s.thNum}>Active PMs</th><th style={s.thNum}>Inspections this month</th>
                <th style={s.thNum}>With defects</th><th style={s.thNum}>Open issues</th><th style={s.thNum}>Renewals overdue</th><th style={s.thNum}>Due soon</th>
              </tr>
            </thead>
            <tbody>
              {yards.map((x) => (
                <tr key={x.yard}>
                  <td style={s.td}>{x.yard}</td>
                  <td style={s.tdTotal}>{pct(x.pms_active - x.pms_overdue, x.pms_active)}</td>
                  <td style={s.tdNum}>
                    {x.pms_overdue ? <DrillCount label={`PMs overdue, ${x.yard}`} onOpen={() => setDrill({ kind: 'pms', yard: x.yard })}>{whole(x.pms_overdue)}</DrillCount> : whole(x.pms_overdue)}
                  </td>
                  <td style={s.tdNum}>{whole(x.pms_active)}</td>
                  <td style={s.tdNum}>{whole(x.inspections_month)}</td>
                  <td style={s.tdNum}>{whole(x.inspections_with_defects_month)}</td>
                  <td style={s.tdNum}>{whole(x.open_inspection_issues)}</td>
                  <td style={s.tdNum}>{whole(x.renewals_overdue)}</td>
                  <td style={s.tdNum}>{whole(x.renewals_due_soon)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function CostsTab({ grain, fleet, yard }) {
  const [view, setView] = useState('category');
  const current = COST_VIEWS.find((v) => v.id === view);
  const filters = useMemo(() => ({
    ...rangeFor(grain), grain, ...(fleet ? { fleet } : {}), ...(yard ? { yard } : {}),
  }), [grain, fleet, yard]);

  const totals = useQuery({
    queryKey: ['fleet-dashboard', 'cost', filters, 'TOTAL'],
    queryFn: () => api.get('/fleet-dashboard/dash-cost', { params: { ...filters, by: 'TOTAL' } }),
  });
  const breakdown = useQuery({
    queryKey: ['fleet-dashboard', 'cost', filters, current.by],
    queryFn: () => api.get('/fleet-dashboard/dash-cost', { params: { ...filters, by: current.by } }),
    enabled: current.by !== 'TOTAL',
  });
  const perUnit = useQuery({
    queryKey: ['fleet-dashboard', 'per-unit', filters],
    queryFn: () => {
      const { from, to } = rangeFor('MONTH');
      return api.get('/fleet-dashboard/dash-cost-per-unit', { params: { from, to, ...(fleet ? { fleet } : {}), ...(yard ? { yard } : {}) } });
    },
    enabled: grain === 'MONTH',
  });

  const rows = useMemo(() => totals.data?.rows || [], [totals.data]);
  const chartData = useMemo(() => rows.map((x) => ({
    period: periodLabel(x.period, grain),
    Planned: n(x.planned_cost),
    Unplanned: n(x.unplanned_cost),
    'Not coded': Math.max(n(x.total_cost) - n(x.planned_cost) - n(x.unplanned_cost), 0),
  })), [rows, grain]);

  const table = useMemo(() => {
    const periods = [...new Set(rows.map((x) => x.period))].slice(-8);
    const source = current.by === 'TOTAL'
      ? CATEGORIES.flatMap(([name, key]) => rows.map((x) => ({ period: x.period, label: name, value: n(x[key]) })))
      : (breakdown.data?.rows || []).map((x) => ({ period: x.period, label: x.label, value: n(x.total_cost) }));
    const byLabel = new Map();
    for (const row of source) {
      if (!byLabel.has(row.label)) byLabel.set(row.label, { label: row.label, total: 0, cells: {} });
      const entry = byLabel.get(row.label);
      entry.total += row.value;
      entry.cells[row.period] = (entry.cells[row.period] || 0) + row.value;
    }
    const list = [...byLabel.values()].sort((a, b) => b.total - a.total);
    const foot = { cells: {}, total: list.reduce((sum, x) => sum + x.total, 0) };
    for (const p of periods) foot.cells[p] = list.reduce((sum, x) => sum + (x.cells[p] || 0), 0);
    return { periods, list, foot };
  }, [rows, breakdown.data, current.by]);

  const latest = rows[rows.length - 1];
  const previous = rows[rows.length - 2];
  // Per truck and per mile from the last full month: the month in progress has few repairs
  // and miles yet, so its figures would read far too low or be missing.
  const thisMonth = `${pacificToday().slice(0, 7)}-01`;
  const unit = (perUnit.data?.rows || []).filter((x) => String(x.month).slice(0, 10) < thisMonth).pop();
  const unitMonth = unit ? periodLabel(unit.month, 'MONTH') : '';
  const inProgress = grain === 'MONTH' && String(latest?.period).slice(0, 10) === thisMonth;
  const failed = totals.error || breakdown.error || perUnit.error;

  if (totals.isLoading) return <p style={s.note}>Loading costs…</p>;
  if (failed) return <p role="alert" style={s.error}>{failed.message || 'The cost figures could not be loaded.'}</p>;
  if (!latest) return <p style={s.note}>No repair costs in this period.</p>;

  const name = periodLabel(latest.period, grain);
  return (
    <>
      <div style={s.tiles}>
        <Tile label={`R&M cost, ${name}${inProgress ? ' so far' : ''}`} value={fmtMoney(n(latest.total_cost))}
          note={previous ? `${periodLabel(previous.period, grain)}: ${fmtMoney(n(previous.total_cost))}` : 'No earlier period'} />
        <Tile label="Unplanned share" value={pct(n(latest.unplanned_cost), n(latest.total_cost))}
          note={`Not coded: ${pct(n(latest.total_cost) - n(latest.planned_cost) - n(latest.unplanned_cost), n(latest.total_cost))}`} />
        <Tile label="Outside vendor share" value={pct(n(latest.vendor_cost), n(latest.total_cost))} note="Repairs naming a vendor" />
        <Tile label="Cost per truck" value={grain === 'MONTH' && unit && n(unit.trucks) > 0 ? fmtMoney(n(unit.rm_cost) / n(unit.trucks)) : '–'}
          note={grain === 'MONTH' ? (unit ? `Vehicles only. ${unitMonth}, ${n(unit.trucks).toLocaleString('en-US')} active` : 'No full month yet') : 'Shown by month'} />
        <Tile label="Cost per mile"
          value={grain === 'MONTH' && unit && n(unit.miles) > 0 ? `$${(n(unit.rm_cost_on_miles) / n(unit.miles)).toFixed(2)}` : '–'}
          note={grain === 'MONTH' ? (unit ? `${unitMonth}, ${n(unit.trucks_with_miles).toLocaleString('en-US')} of ${n(unit.trucks).toLocaleString('en-US')} vehicles report miles` : 'No full month yet') : 'Shown by month'} />
        <Tile label="Cost per engine hour"
          value={grain === 'MONTH' && unit && n(unit.engine_hours) > 0 ? `$${(n(unit.rm_cost_on_hours) / n(unit.engine_hours)).toFixed(2)}` : '–'}
          note={grain === 'MONTH' ? (unit ? `${unitMonth}, ${n(unit.trucks_with_hours).toLocaleString('en-US')} of ${n(unit.trucks).toLocaleString('en-US')} vehicles report hours` : 'No full month yet') : 'Shown by month'} />
      </div>

      <section style={s.panel}>
        <h2 style={s.panelTitle}>Planned against unplanned</h2>
        <p style={s.note}>{grain === 'DAY' ? 'Last 14 days, by completion date.' : `Last 12 months, by completion date.${inProgress ? ' The latest month is still in progress.' : ''}`}</p>
        <div style={{ width: '100%', height: 280, marginTop: sp[3] }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid vertical={false} stroke={c.border} />
              <XAxis dataKey="period" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => (v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : `$${Math.round(v / 1000)}k`)} tick={{ fontSize: 12 }} width={56} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend />
              <Bar dataKey="Planned" stackId="cost" fill={PLANNED} />
              <Bar dataKey="Unplanned" stackId="cost" fill={UNPLANNED} />
              <Bar dataKey="Not coded" stackId="cost" fill={UNKNOWN} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section style={s.panel}>
        <div style={s.panelHead}>
          <h2 style={s.panelTitle}>Where the money goes</h2>
          <Switch options={COST_VIEWS} value={view} onChange={setView} label="Break down cost" />
        </div>
        {breakdown.isFetching ? <p style={s.note}>Loading…</p> : (
          <div style={s.scroll}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>{current.head}</th>
                  {table.periods.map((p) => <th key={p} style={s.thNum}>{periodLabel(p, grain)}</th>)}
                  <th style={s.thNum}>Total</th>
                </tr>
              </thead>
              <tbody>
                {table.list.map((row) => (
                  <tr key={row.label}>
                    <td style={s.td}>{row.label}</td>
                    {table.periods.map((p) => <td key={p} style={s.tdNum}>{fmtMoney(row.cells[p] || 0)}</td>)}
                    <td style={s.tdTotal}>{fmtMoney(row.total)}</td>
                  </tr>
                ))}
                <tr>
                  <td style={s.tdFoot}>All</td>
                  {table.periods.map((p) => <td key={p} style={s.tdFootNum}>{fmtMoney(table.foot.cells[p])}</td>)}
                  <td style={s.tdFootNum}>{fmtMoney(table.foot.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        <p style={s.note}>Total covers every period above, including any that do not fit in the columns.</p>
      </section>
    </>
  );
}

const clock = (v) => new Date(v).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles',
});

// When the numbers were built, and the oldest of the two Fleetio accounts' last syncs.
export function DataAsOf({ rows }) {
  if (!rows?.length) return null;
  const built = rows.map((x) => x.reports_built_at).filter(Boolean).sort().pop();
  const synced = rows.map((x) => x.fleetio_synced_at).filter(Boolean).sort()[0];
  if (!built) return null;
  return (
    <span style={s.asOf}>
      Data as of {clock(built)}{synced ? ` · Fleetio synced ${clock(synced)}` : ''}
    </span>
  );
}

export default function FleetDashboard() {
  const [tab, setTab] = useState('today');
  const [grain, setGrain] = useState('DAY');
  const [fleet, setFleet] = useState('');
  const [yard, setYard] = useState('');
  const yards = useQuery({
    queryKey: ['fleet-dashboard', 'yards'],
    queryFn: () => api.get('/fleet-dashboard/yards'),
    staleTime: 10 * 60 * 1000,
  });
  const freshness = useQuery({
    queryKey: ['fleet-dashboard', 'freshness'],
    queryFn: () => api.get('/fleet-dashboard/dash-freshness'),
    staleTime: 5 * 60 * 1000,
  });
  const yardRows = useMemo(() => yards.data?.rows || [], [yards.data]);
  const seesAll = yards.data?.all === true;
  const canSeeMechanics = yards.data?.mechanics === true;
  // A scoped person starts on their first cost center, since the server refuses "all" for them.
  useEffect(() => {
    if (yards.data && !seesAll && !yard && yardRows.length) setYard(yardRows[0].yard);
  }, [yards.data, seesAll, yard, yardRows]);
  const tabs = TABS.filter((x) => seesAll || x.id !== 'parts');
  const ready = yards.data && (seesAll || yard);

  return (
    <div style={s.page}>
      <PageHeader eyebrow="Fleet" title="R&M Dashboard" subtitle="Is the fleet ready, what is it costing, and what needs action. From Fleetio, updated hourly."
        meta={<DataAsOf rows={freshness.data?.rows} />} />

      <div style={s.controls}>
        <label style={s.control}>Cost center
          <select value={yard} onChange={(e) => setYard(e.target.value)} style={s.select}>
            {seesAll && <option value="">All cost centers</option>}
            {yardRows.map((y) => <option key={y.yard} value={y.yard}>{y.yard}</option>)}
          </select>
        </label>
        <label style={s.control}>Fleet
          <select value={fleet} onChange={(e) => setFleet(e.target.value)} style={s.select}>
            <option value="">Main and MRF</option>
            <option value="MAIN">Main</option>
            <option value="MRF">MRF</option>
          </select>
        </label>
        <div style={{ flex: '1 1 40px' }} />
        <Switch options={[{ id: 'DAY', label: 'Daily' }, { id: 'MONTH', label: 'Monthly' }]} value={grain} onChange={setGrain} label="Period" />
      </div>

      <div role="tablist" aria-label="Dashboard sections" style={s.tabs}>
        {tabs.map((x) => (
          <button key={x.id} type="button" role="tab" aria-selected={tab === x.id} onClick={() => setTab(x.id)}
            style={tab === x.id ? s.tabOn : s.tabOff}>{x.label}</button>
        ))}
      </div>

      {yards.isLoading && <p style={s.note}>Loading…</p>}
      {yards.error && <p role="alert" style={s.error}>{yards.error.message || 'The R&M Dashboard could not be loaded.'}</p>}
      {yards.data && !seesAll && !yardRows.length && (
        <p style={s.note}>None of your cost centers is linked to a Fleetio yard yet. An administrator maps yards under Mappings, Fleet Yards.</p>
      )}

      {ready && tab === 'today' && <TodayTab grain={grain} fleet={fleet} yard={yard} />}
      {ready && tab === 'yards' && <YardsTab grain={grain} fleet={fleet} yard={yard} />}
      {ready && tab === 'costs' && <CostsTab grain={grain} fleet={fleet} yard={yard} />}
      {ready && tab === 'shop' && <ShopTab grain={grain} fleet={fleet} yard={yard} canSeeMechanics={canSeeMechanics} />}
      {ready && tab === 'trucks' && <TrucksTab grain={grain} fleet={fleet} yard={yard} />}
      {ready && seesAll && tab === 'parts' && <PartsTab grain={grain} fleet={fleet} />}
      {ready && tab === 'pm' && <ComplianceTab grain={grain} fleet={fleet} yard={yard} />}
    </div>
  );
}

const cell = { ...t.bodySm, padding: `${sp[2]} ${sp[3]}`, borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' };
// Headers stay visible when a long table scrolls in its own box.
const head = { ...cell, color: c.textMuted, fontWeight: 600, textAlign: 'left', position: 'sticky', top: 0, background: c.surface, zIndex: 1 };
const num = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const tabBase = { border: 0, minHeight: '44px', padding: `0 ${sp[4]}`, background: 'transparent', cursor: 'pointer', fontSize: '14px', borderBottom: '3px solid transparent' };
const button = { border: 0, minHeight: '36px', padding: `0 ${sp[3]}`, borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 };

const s = {
  page: { display: 'flex', flexDirection: 'column', gap: sp[4] },
  controls: { background: c.navy, borderRadius: '12px', padding: `${sp[3]} ${sp[4]}`, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: sp[3] },
  control: { display: 'flex', flexDirection: 'column', gap: sp[1], fontSize: '12px', fontWeight: 600, color: c.onNavy },
  select: { height: '38px', minWidth: '170px', borderRadius: '8px', border: 0, padding: `0 ${sp[2]}`, fontSize: '13px' },
  switch: { display: 'flex', flexWrap: 'wrap', gap: sp[1], background: c.canvas, borderRadius: '8px', padding: sp[1] },
  switchOn: { ...button, background: c.navyDeep, color: c.onNavy },
  switchOff: { ...button, background: c.surface, color: c.navy },
  tabs: { display: 'flex', flexWrap: 'wrap', gap: sp[1], borderBottom: `2px solid ${c.border}` },
  tabOn: { ...tabBase, fontWeight: 700, color: c.navy, borderBottom: `3px solid ${c.brand}` },
  tabOff: { ...tabBase, fontWeight: 500, color: c.textMuted },
  tiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: sp[3] },
  tile: { ...card.base, padding: sp[4] },
  tileLabel: { ...t.caption, fontWeight: 600, color: c.textMuted },
  tileValue: { fontSize: '28px', fontWeight: 700, letterSpacing: '-0.02em', color: c.navy, margin: `${sp[1]} 0` },
  tileNote: { ...t.caption, color: c.textMuted },
  tileButton: { ...card.base, padding: sp[4], textAlign: 'left', cursor: 'pointer', font: 'inherit', display: 'block', width: '100%' },
  tileAction: { ...t.caption, color: c.navy, fontWeight: 600, textDecoration: 'underline', marginTop: sp[1] },
  countButton: { border: 0, background: 'transparent', padding: 0, minHeight: '24px', color: 'inherit', font: 'inherit', fontWeight: 'inherit', cursor: 'pointer', textDecoration: 'underline' },
  link: { color: c.navy, fontWeight: 600 },
  panel: { ...card.base, padding: sp[5], display: 'flex', flexDirection: 'column', gap: sp[2], minWidth: 0 },
  lists: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: sp[3] },
  listRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: sp[3], padding: `${sp[2]} 0`, borderTop: `1px solid ${c.border}` },
  listMain: { ...t.bodySm, fontWeight: 600, color: c.textStrong },
  listSub: { ...t.caption, color: c.textMuted },
  listValue: { ...t.bodySm, fontWeight: 700, color: c.navy, whiteSpace: 'nowrap' },
  boxes: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: sp[3], marginTop: sp[2] },
  box: { ...card.sunk, padding: sp[3] },
  boxName: { ...t.bodySm, fontWeight: 700, color: c.navy, marginBottom: sp[1] },
  boxRow: { ...t.caption, display: 'flex', justifyContent: 'space-between', gap: sp[2], color: c.textMuted, marginTop: '4px' },
  inlineLabel: { ...t.caption, display: 'flex', alignItems: 'center', gap: sp[2], color: c.textMuted, fontWeight: 600 },
  selectLight: { height: '36px', minWidth: '170px', borderRadius: '8px', border: `1px solid ${c.border}`, padding: `0 ${sp[2]}`, fontSize: '13px', background: c.surface },
  closeButton: { border: `1px solid ${c.border}`, background: c.surface, color: c.navy, minHeight: '36px', padding: `0 ${sp[3]}`, borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 },
  panelTall: { ...card.base, padding: sp[5], display: 'flex', flexDirection: 'column', gap: sp[2], minWidth: 0, maxHeight: '520px', overflowY: 'auto' },
  legend: { display: 'flex', flexWrap: 'wrap', gap: sp[3], alignItems: 'center' },
  legendItem: { ...t.caption, display: 'flex', alignItems: 'center', gap: sp[1], color: c.textMuted },
  legendSwatch: { width: '14px', height: '14px', borderRadius: '3px', display: 'inline-block' },
  asOf: { ...t.caption, color: c.textMuted, background: c.canvas, border: `1px solid ${c.border}`, borderRadius: '999px', padding: '3px 10px' },
  panelHead: { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: sp[3] },
  panelTitle: { ...t.h3, color: c.navy, margin: 0 },
  note: { ...t.bodySm, color: c.textMuted, margin: 0 },
  error: { ...t.bodySm, color: c.dangerText, margin: 0 },
  warning: { ...t.bodySm, color: c.warningText, margin: 0 },
  league: { width: '100%', minWidth: '820px', borderCollapse: 'separate', borderSpacing: '3px' },
  leagueOn: { outline: `2px solid ${c.navy}` },
  leagueCell: { ...t.bodySm, textAlign: 'center', fontWeight: 600, padding: `${sp[2]} ${sp[2]}`, borderRadius: '6px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  thCenter: { ...head, textAlign: 'center' },
  tdCenter: { ...cell, textAlign: 'center' },
  scroll: { overflowX: 'auto' },
  scrollTall: { overflow: 'auto', maxHeight: '520px' },
  nameButton: { border: 0, background: 'transparent', padding: 0, minHeight: '32px', color: c.navy, fontWeight: 600, fontSize: '13px', cursor: 'pointer', textDecoration: 'underline', textAlign: 'left' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: head,
  thNum: { ...head, ...num },
  td: cell,
  tdNum: { ...cell, ...num },
  tdTotal: { ...cell, ...num, fontWeight: 600 },
  tdFoot: { ...cell, fontWeight: 700, background: c.surfaceSunk },
  tdFootNum: { ...cell, ...num, fontWeight: 700, background: c.surfaceSunk },
};
